import { RefObject, useEffect, useRef, useState } from 'react'

import * as Sentry from '@sentry/nextjs'

import {
  CodecInfoI,
  MAX_INIT_BUFFER_SIZE,
  combineChunks,
  detectCodecFromInitSegment,
  getCodecInfo,
  getUnsupportedCodecError,
  removeOldBufferedData,
  waitForSourceBufferUpdate,
} from '../utils/mseUtils'

export interface UseMSEPlayerIProps {
  videoRef: RefObject<HTMLVideoElement>
  url: string | null
  headers: Record<string, string> | undefined
  onError?: (error: Error) => void
  onLoadStart?: () => void
  onCanPlay?: () => void
  onAuthError?: () => void
}

export interface UseMSEPlayerReturnIProps {
  isLoading: boolean
  error: string | null
  abort: () => void
  isStreaming: boolean
}

/**
 * Hook for MSE-based video streaming with authenticated fetch
 * Supports both H.264 (avc1) and H.265 (hev1/hvc1) codecs with automatic detection
 */
export const useMSEPlayer = ({
  videoRef,
  url,
  headers,
  onError,
  onLoadStart,
  onCanPlay,
  onAuthError,
}: UseMSEPlayerIProps): UseMSEPlayerReturnIProps => {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isStreaming, setIsStreaming] = useState(false)

  const mediaSourceRef = useRef<MediaSource | null>(null)
  const sourceBufferRef = useRef<SourceBuffer | null>(null)
  const abortControllerRef = useRef<AbortController | null>(null)
  const readerRef = useRef<ReadableStreamDefaultReader | null>(null)
  const loadedDataCleanupRef = useRef<(() => void) | null>(null)

  const teardownStream = () => {
    loadedDataCleanupRef.current?.()
    loadedDataCleanupRef.current = null

    readerRef.current?.cancel().catch(() => {})
    readerRef.current = null

    try {
      abortControllerRef.current?.abort()
    } catch {
      // Ignore
    }
    abortControllerRef.current = null

    try {
      if (mediaSourceRef.current?.readyState === 'open') {
        mediaSourceRef.current.endOfStream()
      }
    } catch {
      // Ignore
    }
    mediaSourceRef.current = null
    sourceBufferRef.current = null

    const video = videoRef.current
    if (video?.src?.startsWith('blob:')) {
      URL.revokeObjectURL(video.src)
      video.src = ''
      video.load()
    }
  }

  const abort = () => {
    teardownStream()
    setIsStreaming(false)
    setIsLoading(false)
  }

  useEffect(() => {
    if (!videoRef.current || !url || !headers) {
      return
    }

    if (!('MediaSource' in window)) {
      const err = new Error('MSE video player: MediaSource API not supported')
      Sentry.captureException(err)
      setError(err.message)
      onError?.(err)
      return
    }

    const video = videoRef.current
    const mediaSource = new MediaSource()
    mediaSourceRef.current = mediaSource
    video.src = URL.createObjectURL(mediaSource)

    const handleError = (
      message: string,
      err?: Error,
      extra?: Record<string, unknown>
    ) => {
      const wrappedErr = new Error(`MSE video player: ${message}`)
      Sentry.captureException(wrappedErr, {
        extra: { originalError: err, ...extra },
      })
      setError(message)
      onError?.(wrappedErr)
      setIsLoading(false)
      setIsStreaming(false)
    }

    const processInitSegment = (data: Uint8Array): CodecInfoI | null => {
      const codecType = detectCodecFromInitSegment(data)

      if (codecType === 'unknown' && data.length < MAX_INIT_BUFFER_SIZE) {
        return null // Need more data
      }

      const info = getCodecInfo(codecType)

      Sentry.addBreadcrumb({
        category: 'mse-player',
        message: `Detected codec: ${codecType}`,
        level: 'info',
        data: {
          codecType,
          mimeType: info.mimeType,
          isSupported: info.isSupported,
        },
      })

      return info
    }

    const appendChunk = async (
      chunk: Uint8Array,
      sourceBuffer: SourceBuffer
    ): Promise<boolean> => {
      if (mediaSource.readyState !== 'open') {
        return false
      }

      await waitForSourceBufferUpdate(sourceBuffer)

      if (mediaSource.readyState !== 'open') {
        return false
      }

      try {
        sourceBuffer.appendBuffer(chunk)
      } catch (err: any) {
        if (err.name === 'QuotaExceededError') {
          await removeOldBufferedData(sourceBuffer, video.currentTime)
          sourceBuffer.appendBuffer(chunk)
        } else {
          throw err
        }
      }

      return true
    }

    const streamLoop = async (
      reader: ReadableStreamDefaultReader<Uint8Array>
    ): Promise<void> => {
      let codecInfo: CodecInfoI | null = null
      let initBuffer: Uint8Array[] = []
      let sourceBuffer: SourceBuffer | null = null

      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { done: isDone, value } = await reader.read()

        if (isDone) {
          if (mediaSource.readyState === 'open') {
            mediaSource.endOfStream()
          }
          setIsLoading(false)
          break
        }

        if (mediaSource.readyState !== 'open') {
          break
        }

        if (!codecInfo) {
          // Phase 1: Accumulate and detect codec from init segment
          initBuffer.push(value)
          const combined = combineChunks(initBuffer)
          codecInfo = processInitSegment(combined)

          if (!codecInfo) {
            continue // Need more data
          }

          if (!codecInfo.isSupported) {
            handleError(getUnsupportedCodecError(codecInfo.type), undefined, {
              codecType: codecInfo.type,
              mimeType: codecInfo.mimeType,
            })
            break
          }

          sourceBuffer = mediaSource.addSourceBuffer(codecInfo.mimeType)
          sourceBufferRef.current = sourceBuffer

          sourceBuffer.addEventListener('error', () => {
            handleError(
              `SourceBuffer error - ${codecInfo!.type} codec failed`,
              undefined,
              { codecType: codecInfo!.type }
            )
          })

          sourceBuffer.appendBuffer(combined)
          initBuffer = []

          await waitForSourceBufferUpdate(sourceBuffer)
          setIsLoading(false)

          // Wait until the browser has decoded at least one frame before
          // signalling readiness.  Without this, consumers that capture a
          // thumbnail (e.g. useVideoFrame) would read an empty/black canvas
          // because videoWidth is still 0 at this point.
          await new Promise<void>((resolve) => {
            if (video.videoWidth > 0) {
              resolve()
              return
            }

            const cleanup = () => {
              video.removeEventListener('loadeddata', onLoaded)
              clearTimeout(timeoutId)
              loadedDataCleanupRef.current = null
            }

            const onLoaded = () => {
              cleanup()
              resolve()
            }

            // Fallback timeout to prevent indefinite stalls if loadeddata
            // never fires (e.g. corrupted init segment, browser quirk).
            const timeoutId = setTimeout(onLoaded, 3000)

            loadedDataCleanupRef.current = () => {
              cleanup()
              resolve()
            }

            video.addEventListener('loadeddata', onLoaded)
          })

          onCanPlay?.()
          continue
        }

        // Phase 2: Stream media data
        const isSuccess = await appendChunk(value, sourceBuffer!)
        if (!isSuccess) {
          break
        }
        await waitForSourceBufferUpdate(sourceBuffer!)
      }
    }

    const startStreaming = async () => {
      const abortController = new AbortController()
      abortControllerRef.current = abortController

      setError(null)
      setIsLoading(true)
      onLoadStart?.()

      const response = await fetch(url, {
        headers,
        mode: 'cors',
        signal: abortController.signal,
      })

      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          if (!onAuthError) {
            setError('Authentication failed')
          }
          setIsLoading(false)
          teardownStream()
          onAuthError?.()
          return
        }
        throw new Error(`HTTP ${response.status}: ${response.statusText}`)
      }

      if (!response.body) {
        throw new Error('ReadableStream not supported')
      }

      const reader = response.body.getReader()
      readerRef.current = reader
      setIsStreaming(true)

      try {
        await streamLoop(reader)
      } finally {
        setIsStreaming(false)
      }
    }

    const handleSourceOpen = async () => {
      try {
        await startStreaming()
      } catch (err: any) {
        if (err.name !== 'AbortError') {
          handleError(err.message, err)
        }
      }
    }

    const handleMediaSourceError = () => {
      handleError('MediaSource error')
    }

    mediaSource.addEventListener('sourceopen', handleSourceOpen)
    mediaSource.addEventListener('error', handleMediaSourceError)

    return teardownStream
  }, [url, headers, videoRef])

  return { isLoading, error, abort, isStreaming }
}
